// English locale. Also the fallback locale and the source of the Dict shape:
// every other locale must implement exactly these keys (see zh.ts).
//
// Interpolation tokens available to every result/copy-pool string:
//   {name}  opponent nickname        {m}     your distance in meters
//   {n}     opponent distance (m)    {d}     absolute distance delta (m)
//   {s}     seconds you needed       {rank}  your rank in the chain
//   {size}  people in the chain

export const en = {
  'app.title': 'Beat Me',
  'app.loading': 'Laying down the track…',
  'app.error': "Couldn't reach the track. Check your connection and try again.",
  'app.retryBoot': 'Try again',

  // --- Intro overlay (design §4.1/§4.2) ---
  'intro.link': '{name} ran {n} m',
  'intro.first': '{name} ran {n} m',
  'intro.walkin': 'The runner before you: {name}, {n} m',
  'intro.rerun': 'Your best: {n} m',
  'intro.tap': 'Tap anywhere to start',
  'intro.hintJump': 'Tap to jump',
  'intro.hintSlide': 'Swipe down to slide',
  'intro.hintLane': "You're the lower lane",
  'intro.hintGhost': "Their ghost runs up top — you're below",
  'intro.flagged': 'That score is under review — you race the official ghost instead',

  // --- HUD ---
  'hud.meters': 'm',
  'hud.ahead': 'ahead',
  'hud.behind': 'behind',
  /** Persistent label on the player's lane in the split-lane view. */
  'lane.you': 'You',
  'mascot.name': 'Trot',
  'lane.pastYou': 'Past you',
  /** Lane label when the ghost is your own weekly best (ladder rung 2). */
  'lane.weeklyYou': 'You this week',
  'ownchain.banner': 'Beat your past self — the new chain starts with you',

  // --- Pause / countdown (design §4.5) ---
  'pause.title': 'Paused',
  'pause.body': 'You left the run. Picking up where you stopped.',
  'countdown.go': 'GO',

  // --- Newbie revive (design §2.6) ---
  'revive.title': 'One free revive',
  'revive.body': 'First run only. No ad, no catch.',
  'revive.accept': 'Get back up ({s})',
  'revive.decline': 'Let it end',

  // --- Ad revive (design §8.1) ---
  // Only ever shown with an ad already loaded (§4.6), so the copy can promise it.
  'revive.ad.title': 'Watch an ad, revive on the spot',
  'revive.ad.accept': 'Watch & revive ({s})',

  // --- Stop-frame tutorial (design §2.3 教学约束, v0.5.2) ---
  // Shown on a frozen frame, with the obstacle already on screen: the copy has
  // to name the gesture AND point at the thing, because "it" is right there.
  'tutorial.jump': 'Tap now — jump it',
  'tutorial.slide': 'Swipe down and hold — slide under',
  /** Quiet result-screen link: run the tutorial again next time. */
  'tutorial.again': 'Tutorial next run',
  'tutorial.armed': 'On',

  // --- Emotion beats (design §2.5) ---
  'beat.overtake': 'You passed {name}',
  'beat.ghostDead': '{name} died here',
  /** Tiny badge on the ghost track where the opponent used a revive. */
  'marker.revive': 'REV',

  // --- Peak moment / the seal (§4bis.22) ---
  /** Lead counter dressing of the HUD gap field, peak arm only. */
  'hud.lead': 'ahead +{d} m',
  /** §4bis.30: the gap meter's third mode, from the duel seal on — ahead is
   *  track nobody in this chain has reached. */
  'hud.territory': 'your ground +{d} m',

  // --- 帮我 The hand-back: the second share verb (§4bis.31, iteration 006) ---
  //
  // Written in English, not translated from the zh — the two voices carry the
  // same idea and neither is a placeholder. The rule for this whole group:
  // **say what you DID, never what you failed at.** 打败我 is a boast and needs
  // a peak to earn it; 帮我 is a gift and is available on every death past
  // 1200 f, so its number is always positive. The button names a PERSON.
  //
  // Timing: planted on the seal (canvas, untappable, interrupts nothing) and
  // CHOSEN at death — because at the seal you would be handing over +0 m. The
  // gift only exists once you have died, and every frame you survive makes it
  // better. That is what turns the death frame from a deflation into the moment
  // you finally have something to give.
  'duel.sealPlant': 'The further you go, the more you hand back.',
  'handoff.cta': 'Hand it to {name} — you added {n} m',
  // A full run's death screen has no "person before you" — you STARTED this
  // life, so it is handed ON, not back. Reusing `handoff.cta` here renders
  // "Hand it to Past you", which is nonsense.
  'handoff.ctaOwn': 'Hand this life on — you ran {n} m',
  'handoff.ctaOwnSub': 'A friend picks it up where you fell.',
  'handoff.label': 'Relay',
  'handoff.open': '{name} handed you this life',
  // Only where YOU start. How far this life has got is already on the headline
  // (openOther) and on the card; saying it twice on one screen drowns the
  // number that is actually doing work here, which is {s}.
  'handoff.openSub': 'Pick it up at {s} m',
  'handoff.openCta': 'Take it',
  'handoff.openOther': 'This life is at {p} m',
  'handoff.openOtherCta': 'Keep it going',
  // The take-over screen's footnote, written for THE PERSON READING IT.
  // This slot used to hold `handoff.ctaSub` ("He picks it up where you fell"),
  // which is addressed to the SHARER — "you" is the person who died. Shown to
  // the recipient it says "where you fell" to somebody who has not run a step.
  'handoff.openNote': 'Every metre you add stays with this life.',
  'handoff.finish': '{k} people. One life. {p} m, finished.',
  'handoff.life': 'This life reached {p} m',
  /** Under the stamp: how to leave the seal for the full result screen. */
  'peak.dismiss': 'Tap anywhere else for the full result',

  // --- Honest silhouettes (§4bis.23) ---
  /** Death-photo two-box chip: an explanation, not a flavor pool — one key. */
  'hud.deathBox': 'Your box clipped its box.',

  // --- 快斗 Blitz Duel (§4bis.28, rewritten by §4bis.30) ---
  // The link opens on a 12 s slice whose LINE is one second past where the
  // sender fell — but the line is not the end of the run. Cross it and you keep
  // going until you die. So the 12 s is what the ask BUYS, not how long you
  // play: the button says what it buys, the sub-line says what happens after.
  // "Take the 12s" promised a 12-second run, and that was not true.
  /** Entry primary: the duel. Secondary: the full run, still the score canon. */
  'duel.cta': 'Take over — 12s out',
  'duel.ctaSub': 'Get past where he fell. Then run as far as you can.',
  'duel.full': 'Run it from the top',
  /** Banner over the frozen countdown frame. */
  'duel.banner': '{name} went down at {d} m',
  /** The aim, under the banner. */
  'duel.aim': 'Outlive his last step. After that the track is yours.',
  /** Marker on the track where the sender's ghost died. */
  'duel.ghostMark': '{name} stopped here',
  /** Untaught devices only: the gesture legend, shown during the longer count. */
  'duel.teach': 'Tap to jump · swipe down to slide',
  /** The countdown's last beat reuses `countdown.go`. */
  /** Result-screen line naming what this run was. */
  'duel.label': '12-second duel',
  /** Instant death: the retry offer, no seal. */
  'duel.retry': 'Again — that one did not count',

  // --- Result screen (design §4.4) ---
  'result.distance': '{m} m',
  'result.deltaSeconds': '{s} more seconds and you had it',
  'result.opponentLine': '{name}: {n} m',
  'result.finished': 'You ran the whole track. Almost nobody does.',
  'result.best': 'Your best: {n} m',
  'result.newBest': 'New record — your old best was {n} m',
  'result.submitting': 'Checking the run…',
  'result.offline': "Offline — your run is saved here and will be sent when you're back.",
  'result.rejected': "The server couldn't verify this run.",
  'result.unsaved': "The server couldn't take this run — it's saved here and will be sent next time.",

  'btn.sendBack': 'Send it back to them',
  'btn.challengeOthers': 'Challenge someone else',
  'btn.retry': 'Run it again',
  'btn.passOn': 'Pass it on first',
  'btn.findSomeone': 'Find someone to beat',
  'btn.judge': 'Bring in a judge',
  /** Link entries only: race your own run and root a fresh chain at yourself. */
  'btn.ownChain': 'Start your own chain',
  'btn.share': 'Share',
  'btn.close': 'Close',

  // --- Rival ladder (§4bis.21) — three beatable rivals, one tap each ---
  // No rewards anywhere in here: the only prize on the ladder is the rival.
  'ladder.title': "Who's next",
  'ladder.rung.opponent': 'The one you just raced',
  'ladder.rung.self': 'You, this week',
  'ladder.rung.daily': "Today's stranger",
  /** Rung 1 after a win or a tie: there is no gap left to name. */
  'ladder.again': 'Beat them again',
  'ladder.gap': '{d} m short',
  /** Rung 2 with no earlier run this week — the rung you fill by running. */
  'ladder.selfEmpty': 'Put a score here first',
  'ladder.line': '{name} · {n} m',
  'ladder.score': '{n} m',
  /** Rung 2 when the record IS the run just finished — say it is fresh, so it
   *  can't be mistaken for the stale previous-round number it used to be. */
  'ladder.selfJustSet': 'The {n} m you just ran',
  /** Rung 2's call to action when you are level with your own record. */
  'ladder.selfBeat': 'Go past it',
  /** Floating banner on the rung-2 rematch: say who the identical ghost is. */
  'ladder.selfBanner': 'Chasing your best week so far',
  'ladder.dailyBanner': "Today's rival: {name}",

  // --- Chain leaderboard (design §5.3) ---
  'chain.title': 'This chain',
  'chain.you': 'you',
  'chain.summary': 'Passed along {size} people · you are #{rank}',
  'chain.pair': "Just the two of you so far. Bring in a third to break the tie.",
  'chain.empty': 'Pull one person in and the chain starts with you.',

  // --- Nickname (design §4.6) ---
  'nickname.title': 'Name yourself',
  'nickname.placeholder': 'Your name',
  'nickname.reroll': 'Roll another',
  'nickname.save': 'Save',
  'nickname.saved': 'Saved',
  'nickname.error': "That one won't work. Try another.",

  // --- Share ---
  'share.copied': 'Link copied',
  'share.failed': 'Copy failed — the link is in the address bar',

  'feedback.link': 'Tell us something',
  'privacy.link': 'Privacy',

  // --- Copy pools (design §4.6). Seeded pick, one per run. ---
  'pool.lose': [
    '{s} more seconds and you had it',
    'One more jump and you were past it',
    '{name} nearly died right where you did',
    '{name} is {d} m ahead, having a quiet laugh',
    'You lost to {name} by {d} m',
    '{d} m. That is the whole story.',
  ],
  'pool.win': [
    '{m} m — {name} is behind you now',
    '{name}, your move',
    'Beat {name} by {d} m. Not by much. Enough.',
    "{name}'s number belongs to you now",
    'You took {name} at {d} m and never looked back',
  ],
  'pool.tie': [
    'You both died on the same meter. That is fate.',
    'Both at {m} m. Nobody won.',
    'Dead even. Run it again to settle it.',
    'Same meter, same second. Rematch.',
    'A true tie with {name}. That almost never happens.',
  ],
  // The default opponent can be the mascot OR a real stranger (design §3.2) —
  // never imply the opponent isn't human.
  'pool.defaultWin': [
    'You passed {name}',
    '{name} is {d} m behind you',
    "{name} can't catch you — go find a friend who might",
    'You beat {name} at {m} m',
    '{name} would like a word — from {d} m back',
  ],
  'pool.defaultLose': [
    '{name} is still ahead',
    '{name} is {d} m up the track',
    '{s} more seconds and you would have caught {name}',
    'Not yet. {name} holds {n} m.',
    '{name} ran {n} m. You ran {m} m.',
  ],
  'pool.rerun': [
    'Your best: {m} m',
    '{m} m. Your own number to beat.',
    'You ran {m} m this time',
  ],
  'pool.shareText': [
    'I ran {m} m. Beat me.',
    '{m} m. Your turn.',
    'Beat my {m} m. Same track, same start.',
    'I got {m} m out of this track. See if you can do better.',
    '{m} m and one very stupid death. Go.',
  ],

  // --- The seal (§4bis.22). Giant type: numbers do the bragging; the opponent
  // is always a person. Stamp lines clamp per src/peak.ts before rendering. ---
  'pool.peakStampWin': [
    // params: name, d (lead), m
    'Beat {name}. By {d} m.',
    '{name} went down. You kept running.',
    "{name}'s record ends here.",
    "Those {d} m used to be {name}'s.",
    '{m} m. Look closely, {name}.',
  ],
  'pool.peakStampNear': [
    // params: s, name
    '{s} s short. One breath.',
    '{s} seconds. One jump.',
    '{name} barely held on.',
    'Hold {s} s longer and it flips.',
  ],
  'pool.peakCtaBack': [
    // win, entry=link: send it back
    'Right now. Send it back to {name}.',
    'Make {name} watch this.',
    'Still hot. Throw it back.',
    'Reply to {name}: you lost.',
  ],
  'pool.peakCtaFresh': [
    // win, walk-in/first/rerun
    'Still hot. Drag someone in.',
    'A run like this deserves a rival.',
    'Send it. See who dares.',
    'Pick someone. Make them run it.',
  ],
  'pool.peakCtaNear': [
    // nearloss vs a real sender; {name} = opponent nickname
    "Tell {name}: {s} s. That's all.",
    "Send it back. Next one won't be close.",
    'Show {name} how close you got.',
    '{s} s short — {name} should worry.',
  ],
  'pool.peakCtaNearWalkin': [
    // nearloss, walk-in/official ghost — no sender to reply to (DR-R1)
    '{s} s short. Send someone to avenge you.',
    'Too close not to show someone.',
    'Send it — see who survives this one.',
    'One breath short. A friend can finish it.',
  ],

  // --- 快斗 Blitz Duel pools (§4bis.28). Params: {name} sender, {d} their
  // metres, {p} yours, {n} the metre gap — short on a loss, PAST on a win
  // (§4bis.30). Written for this language, not translated from the zh — the
  // seal is the loudest type in the game. ---
  'pool.duelWin': [
    'Still standing',
    'Past their hole',
    'Their end. Your halfway.',
    'The line is yours',
  ],
  // §4bis.30: the receipt carries the real margin. Under 004 every winner got
  // the same "+1 second"; this is the first duel line whose number differs
  // between two people who both won.
  'pool.duelWinLine': [
    'He fell at {d} m. You ran to {p} m — {n} m further.',
    '{d} m was his limit. You moved the line to {p} m.',
    'You stood where he did not — and kept going {n} m.',
  ],
  'pool.duelTie': ['Both of you', 'Down together', 'The same step'],
  'pool.duelTieLine': [
    'You died on the step that killed them.',
    'Same hole, same second. Nobody won this.',
  ],
  'pool.duelLose': ['{n} m short', 'Not this time', 'They held it'],
  'pool.duelLoseLine': [
    'They got {d} m. You got {p} m.',
    '{n} m between you and their grave.',
  ],
  'pool.duelInstant': [
    'Gone off the line.',
    'That one barely started.',
    'They ran {d} m, for what it is worth.',
  ],
  'pool.duelCta': [
    'Send one back',
    'Return the favour',
    'Now you hold the hole',
    'The line is at {p} m now. His turn.',
  ],

  // --- The hand-back's three pools (§4bis.31) ---
  // Same seeded `mulberry32(runSeed ^ 0x51ab5ea1)` stream as the duel pools, so
  // the visual baselines stay stable. {d} where he fell, {p} where this life is
  // now, {n} what I added, {name} the person before me.
  // **{n} is an INVARIANT of this pool, not a flourish one line happens to
  // carry.** A life with several authors needs every author to have a claim —
  // the leg superlative, mined from Carry Chain for exactly this. The card is
  // the artifact that actually travels into a chat, so if only one line in
  // three carried the number, roughly two-thirds of hand-back shares would
  // travel without it. Vary the voice freely; the number does not drop out.
  'pool.handoffCard': [
    '{name} fell at {d} m. I carried it {n} m further.',
    "I added {n} m — it's at {p} m now. Your turn.",
    "I put {n} m into this one. Take it, don't let it stop with me.",
  ],
  // The FIRST hop, shared off a full run: nobody else is in this life yet, so
  // not one of these may name a person. (`pool.handoffCard` would render
  // "Past you fell at 0 m" — there is no source run to fall anywhere.)
  // Same invariant. The first hop off a full run has no predecessor, so the
  // stretch you added IS the whole thing — {n} and {p} are the same number
  // here, but it is said as {n} so both card pools read on one rule.
  'pool.handoffCardOwn': [
    "I ran {n} m. Keep it going — don't let it stop here.",
    'I started this life — {n} m. Your turn.',
    'I put {n} m in. Take it and see how far you get.',
  ],
  // The death stamp, on a run that can be handed on (§4bis.31).
  //
  // The default pool contains "You grabbed {d} m out of {name}'s hands" —
  // PLUNDER. Stamping that on the one screen whose primary action is a gift
  // makes it argue with itself: "I took yours" and "here, have this" in the
  // same frame. 帮我's rule is that it says what you DID, and what you did is
  // ADD, not take. Uses only {n} (what you added) and {m} (where the life is),
  // because {d}/{n} do not mean the same thing in `titleFor` and `duelParams`.
  // A STAMP, in the register of the pool it replaces — "Still standing" is 14
  // characters, not a receipt. The first version was receipt-length, wrapped to
  // two lines at 390px and pushed the nickname editor off the bottom edge. The
  // numbers live on the line below and on the CTA, where they already were.
  'pool.handoffStamp': [
    'It went further',
    'Your leg is done',
    'You moved it on',
    'Next runner up',
  ],
  'pool.handoffResult': [
    'Pushed it {n} m further.',
    '{p} m and still going.',
    'You carried it {n} m for him.',
  ],
};

export type Dict = typeof en;
export type DictKey = keyof Dict;
